import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '../../api/tenants';
import { authApi } from '../../api/auth';
import { queryKeys } from '../queryClient';
import type { TenantInput } from '../../types/domain';

/** Platform admin only — the tenant registry (TenantsController), not any single tenant's own
 * subscription/billing data (see useSubscriptions.ts for that). */
export function useTenants() {
  return useQuery({ queryKey: queryKeys.tenants, queryFn: () => tenantsApi.list() });
}

export function useTenant(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tenant(id ?? ''),
    queryFn: () => tenantsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TenantInput) => tenantsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tenants }),
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TenantInput }) => tenantsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tenants }),
  });
}

export function useDeactivateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantsApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tenants }),
  });
}

export function useInviteStoreOwner() {
  return useMutation({
    mutationFn: ({ tenantId, email, name }: { tenantId: string; email: string; name: string }) =>
      authApi.inviteStoreOwner(tenantId, email, name),
  });
}
